import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast/headless'
import { EncryptedName } from '@components/EncryptedContent/EncryptedName'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { UpdateEnsDisplayNameFailed } from '@components/Notifications/UpdateEnsDisplayNameFailed'
import { validateDisplayName, validateUsername } from '@components/SetUsernameForm/validateUsername'
import { TipCounter } from '@components/TipCounter/TipCounter'
import { isDMChannelStreamId, isGDMChannelStreamId } from '@towns-protocol/sdk'
import { Box, Button, Divider, IconButton, Stack, Text, TextButton } from '@ui'
import { useEnsNames } from 'api/lib/ensNames'
import { useDevice } from 'hooks/useDevice'
import { useRouteParams } from 'hooks/useRouteParams'
import { useSetEnsAddress } from 'hooks/useSetEnsAddress'
import { useSetUsername } from 'hooks/useSetUsername'
import { useValidateUsername } from 'hooks/useValidateUsername'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { shortAddress } from 'ui/utils/utils'
import {
    LookupUser,
    useMyUserId,
    useSpaceData,
    useSpaceId,
    useTownsClient,
    useUserLookupStore,
} from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

import { EditableInputField } from './EditableInputField'

export const useCurrentStreamID = () => {
    const { spaceId: spaceIdFromUrl, channelId } = useRouteParams()
    const spaceIdFromContext = useSpaceId()
    const spaceId = spaceIdFromUrl ?? spaceIdFromContext
    if (channelId) {
        if (isDMChannelStreamId(channelId) || isGDMChannelStreamId(channelId)) {
            return channelId
        }
    }
    return spaceId
}

export const SetUsernameDisplayName = (props: { streamId: string }) => {
    const { streamId } = props

    const streamType = useMemo(
        () =>
            isDMChannelStreamId(streamId) ? 'dm' : isGDMChannelStreamId(streamId) ? 'gdm' : 'space',
        [streamId],
    )

    const [showEditFields, setShowEditFields] = useState<boolean>(false)

    const myUserId = useMyUserId()

    const user = useUserLookupStore((s) =>
        streamId && myUserId
            ? streamType === 'dm' || streamType === 'gdm'
                ? s.channelUsers[streamId]?.[myUserId]
                : s.spaceUsers[streamId]?.[myUserId]
            : undefined,
    )
    // const { setSpaceUser } = useUserLookupStore((s) => ({
    //     setSpaceUser: s.setSpaceUser,
    // }))

    const { setUsername } = useSetUsername()
    const { setDisplayName } = useTownsClient()
    const { setEnsAddress } = useSetEnsAddress()
    const [dirtyDisplayName, setDirtyDisplayName] = React.useState<string>(user?.displayName ?? '')
    const [dirtyEns, setDirtyEns] = useState<{ address?: string; name?: string } | undefined>({
        address: user?.ensAddress,
        name: user?.ensName,
    })
    const [isShowingEnsDisplayNameForm, setShowingEnsDisplayNameForm] = useState<boolean>(false)

    const {
        username: dirtyUsername,
        usernameErrorMessage,
        updateUsername,
    } = useValidateUsername({
        streamId,
        defaultUsername: user?.username ?? '',
    })

    const onUsernameChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            updateUsername(e.target.value)
        },
        [updateUsername],
    )

    useEffect(() => {
        if (user?.username) {
            updateUsername(user.username)
        }
        if (user?.displayName) {
            setDirtyDisplayName(user.displayName)
        }
        setDirtyEns({ address: user?.ensAddress, name: user?.ensName })
    }, [
        user?.username,
        user?.displayName,
        user?.ensAddress,
        user?.ensName,
        updateUsername,
        setDirtyDisplayName,
    ])

    // const setOptimisticEns = useCallback(
    //     async (ensAddress: string | undefined, ensName: string | undefined) => {
    //         if (!streamId || !myUserId) {
    //             return
    //         }

    //         setSpaceUser(
    //             myUserId,
    //             {
    //                 ...user,
    //                 ensAddress,
    //                 ensName,
    //             },
    //             streamId,
    //         )
    //     },
    //     [myUserId, setSpaceUser, streamId, user],
    // )

    const onSave = useCallback(async () => {
        if (!streamId) {
            return
        }
        const trimmedUsername = dirtyUsername.trimEnd()
        const trimmedDisplayName = dirtyDisplayName.trim()

        const usernameValidation = validateUsername(trimmedUsername)
        const displayNameValidation = validateDisplayName(trimmedDisplayName)

        if (trimmedUsername !== user?.username && usernameValidation.valid) {
            await setUsername(streamId, trimmedUsername)
        }
        if (trimmedDisplayName !== user?.displayName && displayNameValidation.valid) {
            await setDisplayName(streamId, trimmedDisplayName)
        }
        if (dirtyEns?.address !== user?.ensAddress || dirtyEns?.name !== user?.ensName) {
            // const originalEns = {
            //     address: user?.ensAddress,
            //     name: user?.ensName,
            // }

            // setOptimisticEns(dirtyEns?.address, dirtyEns?.name)

            setEnsAddress(streamId, dirtyEns?.address).catch(() => {
                // setOptimisticEns(originalEns.address, originalEns.name)
                toast.custom((t) => (
                    <UpdateEnsDisplayNameFailed
                        toast={t}
                        onClick={() => setShowingEnsDisplayNameForm(true)}
                    />
                ))
            })
        }
        setShowEditFields(false)
    }, [
        user,
        setUsername,
        streamId,
        dirtyUsername,
        dirtyDisplayName,
        dirtyEns,
        setDisplayName,
        setEnsAddress,
        setShowEditFields,
        setShowingEnsDisplayNameForm,
    ])

    const onCancel = useCallback(() => {
        updateUsername(user?.username ?? '')
        setDirtyDisplayName(user?.displayName ?? '')
        setDirtyEns({ address: user?.ensAddress, name: user?.ensName })
        setShowEditFields(false)
    }, [setDirtyDisplayName, updateUsername, setShowEditFields, user, setDirtyEns])

    const onDisplayNameChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setDirtyDisplayName(e.target.value)
        },
        [setDirtyDisplayName],
    )

    // const onSetEnsNameClicked = useCallback(() => {
    //     setShowingEnsDisplayNameForm(true)
    // }, [setShowingEnsDisplayNameForm])

    const displayNameErrorMessage = useMemo(
        () => validateDisplayName(dirtyDisplayName).message,
        [dirtyDisplayName],
    )

    const usernameError = useMemo(() => {
        if (dirtyUsername === user?.username) {
            return undefined
        }
        return usernameErrorMessage
    }, [dirtyUsername, usernameErrorMessage, user])

    const saveButtonDisabled = useMemo(() => {
        const trimmedUsername = dirtyUsername.trimEnd()
        const trimmedDisplayName = dirtyDisplayName.trim()

        const isUsernameUnchanged = user?.username === trimmedUsername
        const isDisplayNameUnchanged = user?.displayName === trimmedDisplayName
        const isEnsUnchanged =
            user?.ensAddress === dirtyEns?.address && user?.ensName === dirtyEns?.name

        const isUsernameInvalid = !validateUsername(trimmedUsername).valid
        const isDisplayNameInvalid = !validateDisplayName(trimmedDisplayName).valid

        return (
            (isUsernameUnchanged && isDisplayNameUnchanged && isEnsUnchanged) ||
            (!isUsernameUnchanged && isUsernameInvalid) ||
            (!isDisplayNameUnchanged && isDisplayNameInvalid)
        )
    }, [user, dirtyDisplayName, dirtyUsername, dirtyEns])

    const saveOnEnter = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && !saveButtonDisabled) {
                event.preventDefault()
                onSave()
            }
        },
        [onSave, saveButtonDisabled],
    )

    /**
     * saving this in case we actually need it - I think "in this DM" is enough
     */

    // const { memberIds } = useMembers(streamId)
    // const dmCounterPartyName = useMemo(() => {
    //     if (isDMChannelStreamId(streamId)) {
    //         const counterPartyId =
    //             memberIds.find((m) => m !== myUserId) ??
    //             /* last option, you can be in a dm with yourself */
    //             memberIds[0]
    //         if (counterPartyId) {
    //             return useUserLookupStore.getState().channelUsers[streamId]?.[counterPartyId]
    //         }
    //     }
    //     return ''
    // }, [memberIds, myUserId, streamId])

    const spaceData = useSpaceData()

    const spaceName = spaceData?.id === streamId ? spaceData?.name : undefined

    const titlePrefix = useMemo(() => {
        switch (streamType) {
            case 'space':
                return `In ${spaceName ?? 'this space'}`
            case 'dm':
                return `In this DM`
            case 'gdm':
                return `In this group`
        }
    }, [spaceName, streamType])

    return (
        <Stack padding gap="paragraph" background="level2" rounded="sm">
            {user || !showEditFields ? (
                <Box horizontal justifyContent="spaceBetween" alignItems="end">
                    <Text size="sm" color="gray2">
                        {user ? (
                            <>{titlePrefix}, you appear as:</>
                        ) : (
                            <>{titlePrefix}, you don&apos;t have a specific username</>
                        )}
                    </Text>
                    {user && !showEditFields && (
                        <TextButton
                            data-testid="edit-username-button"
                            onClick={() => setShowEditFields(true)}
                        >
                            Edit
                        </TextButton>
                    )}
                </Box>
            ) : (
                <></>
            )}

            {showEditFields ? (
                <Stack gap paddingTop="xs">
                    <Stack gap="xxs">
                        <Box horizontal justifyContent="spaceBetween" alignItems="center">
                            <Text size="sm" color="default">
                                Display name
                            </Text>
                            {/* <TextButton size="sm" color="cta1" onClick={onSetEnsNameClicked}>
                                {user?.ensAddress
                                    ? 'Edit ENS Display Name'
                                    : 'Add ENS Display Name'}
                            </TextButton> */}
                        </Box>
                        <EditableInputField
                            title=""
                            value={dirtyDisplayName}
                            error={displayNameErrorMessage}
                            placeholder="Enter display name"
                            dataTestId="edit-display-name-input-field"
                            maxLength={32}
                            onKeyDown={saveOnEnter}
                            onChange={onDisplayNameChange}
                        />
                    </Stack>

                    <EditableInputField
                        title="Username"
                        value={dirtyUsername}
                        error={usernameError}
                        maxLength={16}
                        placeholder="Enter username"
                        dataTestId="edit-username-input-field"
                        onKeyDown={saveOnEnter}
                        onChange={onUsernameChange}
                    />
                </Stack>
            ) : user ? (
                <>
                    {user ? (
                        <Stack horizontal gap="xxs" alignItems="center">
                            <UsernameDisplayNameContent user={user} />
                        </Stack>
                    ) : (
                        <></>
                    )}
                    {user ? <UsernameDisplayNameEncryptedContent user={user} /> : <></>}
                </>
            ) : (
                <></>
            )}
            {!showEditFields && (
                <Stack horizontal gap="sm" paddingTop="xs">
                    {!user && (
                        <Button
                            tone="level3"
                            size="button_sm"
                            width="auto"
                            color="default"
                            onClick={() => {
                                setShowEditFields(true)
                            }}
                        >
                            Add username
                        </Button>
                    )}
                </Stack>
            )}
            {showEditFields && (
                <Stack horizontal>
                    <Box grow />

                    <Stack horizontal gap="sm" alignItems="center">
                        <Button
                            size="button_sm"
                            tone="level3"
                            rounded="full"
                            data-testid="edit-username-panel-cancel-button"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="button_sm"
                            tone="cta1"
                            disabled={saveButtonDisabled}
                            data-testid="edit-username-panel-save-button"
                            rounded="full"
                            onClick={onSave}
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            )}
            {isShowingEnsDisplayNameForm && (
                <EnsDisplayNameModal
                    spaceId={spaceData?.id}
                    streamId={streamId}
                    currentEns={{ address: user?.ensAddress, name: user?.ensName }}
                    setShowingEnsDisplayNameForm={setShowingEnsDisplayNameForm}
                    onHide={() => setShowingEnsDisplayNameForm(false)}
                    onEnsSelected={(ens) => {
                        setDirtyEns(ens)
                    }}
                />
            )}
        </Stack>
    )
}

const UsernameDisplayNameContent = (props: { user: LookupUser }) => {
    const { user } = props

    return user.usernameEncrypted && user.displayNameEncrypted ? (
        <>
            <Text
                fontSize="lg"
                fontWeight="strong"
                color="default"
                data-testid="current-username-encrypted"
            >
                {shortAddress(user.userId)}
            </Text>
        </>
    ) : (
        <Stack justifyContent="center" gap="sm">
            {!user.displayNameEncrypted && user.displayName.length > 0 && (
                <Stack horizontal gap="sm" alignItems="center" height="x1.5">
                    <Text
                        color="default"
                        fontSize="lg"
                        fontWeight="strong"
                        data-testid="current-display-name-decrypted"
                    >
                        {getPrettyDisplayName(user)}
                    </Text>
                    <TipCounter userId={user.userId} />
                </Stack>
            )}
            {!user.usernameEncrypted && (
                <Stack horizontal gap="xxs" alignItems="center">
                    {user.username.length > 0 && (
                        <Text
                            color={user.displayName ? 'gray2' : 'default'}
                            data-testid="current-username-decrypted"
                        >
                            {user.displayName ? '@' : ''}
                            {user.username}
                        </Text>
                    )}
                    {!user.displayName && <TipCounter userId={user.userId} />}
                </Stack>
            )}
        </Stack>
    )
}

const UsernameDisplayNameEncryptedContent = (props: { user: LookupUser }) => {
    const { user } = props
    return user.usernameEncrypted && user.displayNameEncrypted ? (
        <EncryptedName message="Your username and display name are still decrypting." />
    ) : user.displayNameEncrypted ? (
        <EncryptedName message="Your display name is still decrypting." />
    ) : user.usernameEncrypted ? (
        <EncryptedName message="Your username is still decrypting." />
    ) : (
        <></>
    )
}

const EnsDisplayNameModal = (props: {
    streamId: string
    spaceId: string | undefined
    currentEns?: { address?: string; name?: string }
    onHide: () => void
    setShowingEnsDisplayNameForm: (show: boolean) => void
    onEnsSelected?: (ens: { address?: string; name?: string } | undefined) => void
}) => {
    const { streamId, onHide, currentEns, setShowingEnsDisplayNameForm, onEnsSelected } = props
    const { ensNames, isFetching } = useEnsNames()
    const { setEnsAddress } = useSetEnsAddress()
    const myUserId = useMyUserId()
    // const user = useUserLookupStore((s) =>
    //     spaceId && myUserId ? s.spaceUsers?.[spaceId]?.[myUserId] : undefined,
    // )
    // const { lookupUser, setSpaceUser } = useUserLookupStore((s) => ({
    //     lookupUser: s.lookupUser,
    //     setSpaceUser: s.setSpaceUser,
    // }))

    const { openPanel } = usePanelActions()
    const [selectedEns, setSelectedEns] = useState<{ address?: string; name?: string } | undefined>(
        currentEns,
    )
    const { isTouch } = useDevice()

    const onViewLinkedWalletsClick = useCallback(() => {
        onHide()
        openPanel(CHANNEL_INFO_PARAMS.WALLETS)
    }, [onHide, openPanel])

    const hasEnsName = ensNames.length > 0 && !isFetching

    // const setOptimisticEns = useCallback(
    //     async (ensAddress: string | undefined, ensName: string | undefined) => {
    //         if (!streamId || !myUserId) {
    //             return
    //         }
    //         setSpaceUser(
    //             myUserId,
    //             {
    //                 ...user,
    //                 ensAddress,
    //                 ensName,
    //             },
    //             spaceId,
    //         )
    //     },
    //     [myUserId, setSpaceUser, spaceId, streamId, user],
    // )

    const onSave = useCallback(() => {
        if (!myUserId) {
            onHide()
            return
        }

        if (onEnsSelected) {
            onEnsSelected(selectedEns)
            onHide()
            return
        }

        // const userSnapshot = lookupUser(myUserId)
        // const oldEns = { address: userSnapshot?.ensAddress, name: userSnapshot?.ensName }

        // Ideally, we would like to queue those updates and do it later
        if (!streamId) {
            // setOptimisticEns(oldEns.address, oldEns.name)
            toast.custom((t) => (
                <UpdateEnsDisplayNameFailed
                    toast={t}
                    onClick={() => setShowingEnsDisplayNameForm(true)}
                />
            ))
            onHide()
            return
        }
        // setOptimisticEns(selectedEns?.address, selectedEns?.name)
        setEnsAddress(streamId, selectedEns?.address).catch(() => {
            // setOptimisticEns(oldEns.address, oldEns.name)
            toast.custom((t) => (
                <UpdateEnsDisplayNameFailed
                    toast={t}
                    onClick={() => setShowingEnsDisplayNameForm(true)}
                />
            ))
        })
        onHide()
    }, [
        myUserId,
        streamId,
        selectedEns,
        setEnsAddress,
        onHide,
        setShowingEnsDisplayNameForm,
        onEnsSelected,
    ])

    return (
        <ModalContainer asSheet maxWidth="400" onHide={onHide}>
            {!isTouch && (
                <Box position="relative">
                    <IconButton position="topRight" icon="close" onClick={onHide} />
                </Box>
            )}
            <Stack gap alignItems="center" paddingTop="lg">
                <Text size="lg" fontWeight="strong" color="default">
                    Set ENS Display Name
                </Text>
            </Stack>

            <Stack gap alignItems="center" paddingTop="lg">
                {isFetching && <ButtonSpinner />}
                {hasEnsName ? (
                    <Stack scroll alignContent="start" width="100%" gap="sm">
                        {ensNames.map((ensName) => (
                            <EnsRow
                                key={ensName.address}
                                label={ensName.name}
                                checked={ensName.address === selectedEns?.address}
                                onClick={() =>
                                    setSelectedEns({
                                        address: ensName.address,
                                        name: ensName.name,
                                    })
                                }
                            />
                        ))}

                        <EnsRow
                            key="no-name"
                            label="None"
                            checked={selectedEns?.address === undefined}
                            onClick={() => setSelectedEns(undefined)}
                        />
                    </Stack>
                ) : (
                    <>
                        <Box grow centerContent padding>
                            <Text color="gray2" textAlign="center">
                                No ENS names were found in your wallets. <br />
                                Link a new name to set a verified ENS as your display name:
                            </Text>
                        </Box>
                    </>
                )}
            </Stack>

            <Stack gap shrink={false} paddingTop="sm">
                {hasEnsName && (
                    <>
                        <Button tone="cta1" width="100%" onClick={onSave}>
                            Set ENS
                        </Button>
                        <Stack horizontal gap="sm" alignItems="center" width="100%">
                            <Divider />
                            <Text shrink={false} color="gray2" fontSize="sm">
                                Or add a verified ENS
                            </Text>
                            <Divider />
                        </Stack>
                    </>
                )}
                <Button
                    tone={hasEnsName ? 'level2' : 'cta1'}
                    width="100%"
                    onClick={onViewLinkedWalletsClick}
                >
                    {hasEnsName ? 'View Linked Wallets' : 'Link a Wallet'}
                </Button>
            </Stack>
        </ModalContainer>
    )
}

const EnsRow = (props: { label: string | undefined; checked: boolean; onClick: () => void }) => {
    const { label: ensName, onClick, checked } = props
    return (
        <Stack
            hoverable
            horizontal
            padding
            alignItems="center"
            gap="sm"
            background={{ default: 'level2', hover: 'level3' }}
            rounded="sm"
            onClick={onClick}
        >
            <Text size="md" color="default">
                {ensName}
            </Text>
            <Box grow />
            <CircleInCircle checked={checked} />
        </Stack>
    )
}

const CircleInCircle = (props: { checked: boolean }) => {
    const { checked } = props
    return (
        <Box centerContent rounded="full" padding="sm" width="x2" height="x2" background="level4">
            {checked && (
                <Box width="x2" height="x2" background="inverted" rounded="full" shrink={false} />
            )}
        </Box>
    )
}
